import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApi } from '../../adminApi';
import { IconActivity, IconArrowRight } from '../../components/Icon';
import { AdminHeader } from './ui';

/**
 * Admin dashboard — every tile, sparkline, plan-mix ring, and live-status
 * signal here reads from `/admin/stats`. No hardcoded arrays, no marketing
 * status strings. Refreshes every 30s while the tab is visible.
 */

type Stats = {
  total?: number; pro?: number; free?: number;
  banned?: number; testers?: number; today?: number;
  gold?: number; platinum?: number;
  revenue?: number; revenue30?: number;
  pending?: number; paid?: number; failed?: number;
  servers?: number; coupons?: number; alerts?: number;
  contacts?: number; contacts24?: number;
  signups14?: number[];
  lastSignupAt?: string | null;
  lastInvoiceAt?: string | null;
  serverTime?: string;
};

function fmtRelative(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(t)) return String(iso);
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number>(0);

  const load = () => {
    adminApi.stats()
      .then(r => {
        if (r.status === 200) {
          const s: Stats = r.data?.stats || r.data || {};
          setStats(s); setErr(null); setLoadedAt(Date.now());
        } else setErr(r.data?.message || 'Failed to load stats');
      })
      .catch(e => setErr(String(e)));
  };

  useEffect(() => {
    load();
    const id = window.setInterval(() => { if (!document.hidden) load(); }, 30_000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const spark = useMemo(() => {
    const arr = Array.isArray(stats?.signups14) && stats!.signups14!.length ? stats!.signups14! : [];
    // Pad / trim to exactly 14 points so the axis math stays stable even
    // when a legacy backend response is short.
    const out = [...arr];
    while (out.length < 14) out.unshift(0);
    return out.slice(-14);
  }, [stats]);

  if (err) {
    return (
      <div className="glass p-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-bold text-red-400">Couldn't load dashboard</div>
          <div className="text-xs text-[color:var(--color-ink-dim)] mt-1">{err}</div>
        </div>
        <button onClick={load} className="btn btn-ghost !py-2 !px-3 text-xs">Retry</button>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="flex items-center gap-3 text-sm text-[color:var(--color-ink-faint)]">
        <span className="w-2 h-2 rounded-full bg-[color:var(--color-brand)] animate-pulse" /> Loading dashboard…
      </div>
    );
  }

  const s = stats;
  const total = Number(s.total || 0);
  const pro = Number(s.pro || 0);
  const free = Number(s.free ?? Math.max(0, total - pro));
  const gold = Number(s.gold || 0);
  const platinum = Number(s.platinum || 0);
  // Reconcile: if backend didn't split gold/platinum but did report pro,
  // fall back to assuming everything is gold so the ring still adds to 100.
  const goldRes = gold || (pro && !platinum ? pro : 0);
  const platRes = platinum || 0;
  const freeRes = Math.max(0, total - goldRes - platRes);

  const cards = [
    { label: 'Total users',    value: total,                                          sub: `${pro} pro · ${free} free`,                             to: '/admin/users',                  grad: 'from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)]' },
    { label: 'Revenue (all)',  value: `$${Number(s.revenue || 0).toFixed(2)}`,        sub: `30d $${Number(s.revenue30 || 0).toFixed(2)} · ${Number(s.paid || 0)} paid · ${Number(s.pending || 0)} pending`, to: '/admin/payments',                grad: 'from-emerald-500 to-teal-600' },
    { label: 'Banned',         value: Number(s.banned || 0),                          sub: `${Number(s.testers || 0)} testers`,                      to: '/admin/users?filter=banned',     grad: 'from-red-500 to-rose-700' },
    { label: 'Today',          value: `+${Number(s.today || 0)}`,                     sub: 'new signups',                                            to: '/admin/users',                  grad: 'from-violet-500 to-fuchsia-600' },
    { label: 'Servers',        value: s.servers ?? '—',                               sub: 'active panels',                                          to: '/admin/servers',                grad: 'from-blue-500 to-cyan-600' },
    { label: 'Coupons',        value: s.coupons ?? '—',                               sub: 'active codes',                                           to: '/admin/coupons',                grad: 'from-amber-500 to-orange-600' },
    { label: 'Contacts',       value: Number(s.contacts || 0),                        sub: `${Number(s.contacts24 || 0)} in 24h`,                    to: '/admin/contacts',               grad: 'from-pink-500 to-rose-600' },
    { label: 'Alerts',         value: s.alerts ?? '—',                                sub: 'system banners',                                         to: '/admin/alerts',                 grad: 'from-slate-600 to-slate-800' },
  ];

  // Sparkline geometry — computed from the actual data, not a hardcoded scale.
  const max = Math.max(1, ...spark);
  const w = 300, h = 60;
  const step = w / Math.max(1, spark.length - 1);
  const yFor = (v: number) => h - Math.round((v / max) * (h - 6)) - 3;
  const linePath = spark.map((v, i) => `${i === 0 ? 'M' : 'L'}${(step * i).toFixed(1)},${yFor(v)}`).join(' ');
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  const spark14Total = spark.reduce((a, b) => a + b, 0);

  // Plan-mix ring — real percentages
  const denom = Math.max(1, freeRes + goldRes + platRes);
  const pFree = Math.round((freeRes / denom) * 100);
  const pGold = Math.round((goldRes / denom) * 100);
  const pPlat = Math.max(0, 100 - pFree - pGold);
  const ringBg = `conic-gradient(#E8476A 0% ${pGold}%, #A11A35 ${pGold}% ${pGold + pPlat}%, #2A1F24 ${pGold + pPlat}% 100%)`;

  return (
    <div>
      <AdminHeader
        icon={IconActivity}
        title="Dashboard"
        subtitle="Live · signed HMAC + MFA · auto-refreshes every 30s"
        actions={
          <div className="flex items-center gap-2 text-xs">
            <span className="chip"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-ok)] animate-pulse" /> Live</span>
            {loadedAt > 0 && <span className="text-[color:var(--color-ink-faint)]">updated {fmtRelative(new Date(loadedAt).toISOString())}</span>}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Link to={c.to} className="glass p-6 block hover:border-[color:var(--color-brand)]/30 transition group overflow-hidden relative">
              <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${c.grad} opacity-20 blur-2xl group-hover:opacity-30 transition`} />
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--color-ink-faint)] font-bold">{c.label}</div>
              <div className="h-title text-3xl font-extrabold mt-1">{c.value}</div>
              <div className="text-xs text-[color:var(--color-ink-dim)] mt-1">{c.sub}</div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3 mt-6">
        <div className="glass p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">Signups — last 14 days</h3>
            <span className="text-xs text-[color:var(--color-ink-faint)]">total {spark14Total} · peak {max}</span>
          </div>
          <div className="mt-4 h-24 relative">
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#E8476A" stopOpacity="0.35" />
                  <stop offset="1" stopColor="#E8476A" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#sg)" />
              <path d={linePath} fill="none" stroke="#FFB0C1" strokeWidth="1.6" />
              {spark.map((v, i) => (
                <circle key={i} cx={(step * i).toFixed(1)} cy={yFor(v)} r="1.6" fill="#FFB0C1" />
              ))}
            </svg>
          </div>
          <div className="flex justify-between text-[10px] text-[color:var(--color-ink-faint)] mt-1">
            <span>14d ago</span><span>Today ({spark[spark.length - 1] ?? 0})</span>
          </div>
        </div>

        <div className="glass p-6">
          <h3 className="font-bold">Plan mix</h3>
          <div className="mt-4 flex items-center gap-4">
            <div className="w-24 h-24 rounded-full p-1" style={{ background: ringBg }}>
              <div className="w-full h-full rounded-full bg-[color:var(--color-surface)] grid place-items-center text-xs font-bold">Free {pFree}%</div>
            </div>
            <div className="text-xs space-y-1">
              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#2A1F24]" /> Free · {freeRes} · {pFree}%</div>
              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#E8476A]" /> Gold · {goldRes} · {pGold}%</div>
              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#A11A35]" /> Platinum · {platRes} · {pPlat}%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mt-6">
        <div className="glass p-6">
          <h3 className="font-bold">Quick actions</h3>
          <div className="grid gap-2 mt-4">
            <Link to="/admin/users" className="btn btn-ghost justify-between">Manage users <IconArrowRight width={14} height={14} /></Link>
            <Link to="/admin/servers" className="btn btn-ghost justify-between">Servers &amp; panels <IconArrowRight width={14} height={14} /></Link>
            <Link to="/admin/apps" className="btn btn-ghost justify-between">Publish app build <IconArrowRight width={14} height={14} /></Link>
            <Link to="/admin/coupons" className="btn btn-ghost justify-between">Create coupon <IconArrowRight width={14} height={14} /></Link>
            <Link to="/admin/email" className="btn btn-ghost justify-between">Email / Broadcast <IconArrowRight width={14} height={14} /></Link>
          </div>
        </div>
        <div className="glass p-6">
          <h3 className="font-bold">System health</h3>
          <ul className="text-sm text-[color:var(--color-ink-dim)] mt-3 space-y-2">
            <li className="flex items-center justify-between gap-2">
              <span>Last signup</span>
              <span className="font-mono text-xs text-[color:var(--color-ink)]">{fmtRelative(s.lastSignupAt)}</span>
            </li>
            <li className="flex items-center justify-between gap-2">
              <span>Last invoice</span>
              <span className="font-mono text-xs text-[color:var(--color-ink)]">{fmtRelative(s.lastInvoiceAt)}</span>
            </li>
            <li className="flex items-center justify-between gap-2">
              <span>Pending invoices</span>
              <span className={`font-mono text-xs ${Number(s.pending || 0) > 0 ? 'text-[color:var(--color-warn)]' : 'text-[color:var(--color-ok)]'}`}>{Number(s.pending || 0)}</span>
            </li>
            <li className="flex items-center justify-between gap-2">
              <span>Failed invoices</span>
              <span className={`font-mono text-xs ${Number(s.failed || 0) > 0 ? 'text-[color:var(--color-danger)]' : 'text-[color:var(--color-ok)]'}`}>{Number(s.failed || 0)}</span>
            </li>
            <li className="flex items-center justify-between gap-2">
              <span>Contacts (24h)</span>
              <span className="font-mono text-xs text-[color:var(--color-ink)]">{Number(s.contacts24 || 0)}</span>
            </li>
            <li className="flex items-center justify-between gap-2">
              <span>Active servers</span>
              <span className={`font-mono text-xs ${Number(s.servers || 0) > 0 ? 'text-[color:var(--color-ok)]' : 'text-[color:var(--color-danger)]'}`}>{Number(s.servers || 0)}</span>
            </li>
            <li className="flex items-center justify-between gap-2">
              <span>Server time</span>
              <span className="font-mono text-xs text-[color:var(--color-ink-dim)]">{s.serverTime || '—'}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
